import { useEffect, useMemo, useState } from "react";
import { addDays, addWeeks, format, startOfWeek } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { CalendarDays, Check, ChevronLeft, ChevronRight, Clock, Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { trialProspectsApi, type TrialSlot } from "@/services/edgeFunctionWrappers/trialProspectsWrapper";
import { useUserStore } from "@/store/userStore";

export interface TrialSlotSelection {
  classId: string;
  classDate: string;
  time: string;
  name: string;
}

interface TrialSlotCalendarProps {
  value: TrialSlotSelection | null;
  onChange: (slot: TrialSlotSelection) => void;
  initialDate?: Date;
  currentSlot?: { classId: string; classDate: string };
  futureOnly?: boolean;
}

export function TrialSlotCalendar({
  value,
  onChange,
  initialDate,
  currentSlot,
  futureOnly = false,
}: TrialSlotCalendarProps) {
  const { t, i18n } = useTranslation("trials");
  const gymId = useUserStore((state) => state.gymId);
  const userId = useUserStore((state) => state.user?.id);
  const initialDateTimestamp = initialDate?.getTime() ?? null;
  const [weekStart, setWeekStart] = useState(() => startOfWeek(initialDate ?? new Date(), { weekStartsOn: 1 }));
  const [classes, setClasses] = useState<TrialSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const locale = i18n.language === "fr" ? fr : enUS;

  useEffect(() => {
    if (initialDateTimestamp === null) return;
    const nextWeekStart = startOfWeek(new Date(initialDateTimestamp), { weekStartsOn: 1 });
    setWeekStart((currentWeekStart) =>
      currentWeekStart.getTime() === nextWeekStart.getTime() ? currentWeekStart : nextWeekStart
    );
  }, [initialDateTimestamp]);

  const normalizedWeekDates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );

  useEffect(() => {
    if (!gymId || !userId) return;
    let cancelled = false;
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    setLoading(true);
    trialProspectsApi.listSlots(gymId, format(weekStart, "yyyy-MM-dd"), format(end, "yyyy-MM-dd"))
      .then((result) => { if (!cancelled) setClasses(result); })
      .catch(() => { if (!cancelled) setClasses([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [gymId, userId, weekStart]);

  const today = format(new Date(), "yyyy-MM-dd");
  const byDate = useMemo(() => {
    const map = new Map<string, TrialSlot[]>();
    for (const scheduledClass of classes) {
      const classDate = scheduledClass.class_date;
      if (!classDate || (futureOnly && classDate < today)) continue;
      const current = currentSlot?.classId === scheduledClass.id && currentSlot.classDate === classDate;
      const available = scheduledClass.available;
      if (available <= 0 && !current) continue;
      const day = map.get(classDate) ?? [];
      day.push(scheduledClass);
      map.set(classDate, day);
    }
    map.forEach((items) => items.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [classes, currentSlot, futureOnly, today]);

  const visibleDays = normalizedWeekDates.filter((date) => (byDate.get(format(date, "yyyy-MM-dd"))?.length ?? 0) > 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" size="icon" onClick={() => setWeekStart(addWeeks(weekStart, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 text-center text-sm font-medium">
          {format(normalizedWeekDates[0], "d MMM", { locale })} – {format(normalizedWeekDates[6], "d MMM yyyy", { locale })}
        </div>
        <Button type="button" variant="outline" size="icon" onClick={() => setWeekStart(addWeeks(weekStart, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="max-h-[42dvh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("loadingSlots")}
          </div>
        ) : visibleDays.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">{t("rescheduleDialog.noSlots")}</div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {visibleDays.map((date) => {
              const dateKey = format(date, "yyyy-MM-dd");
              return (
                <section key={dateKey} className="overflow-hidden rounded-md border bg-card">
                  <div className={cn("border-b px-2 py-1.5 text-center text-xs font-medium uppercase", dateKey === today ? "bg-primary/10 text-primary" : "bg-muted/50 text-muted-foreground")}> 
                    <div>{format(date, "EEE", { locale })}</div>
                    <div className="text-base font-bold text-foreground">{format(date, "d", { locale })}</div>
                  </div>
                  <div className="space-y-1 p-1.5">
                    {(byDate.get(dateKey) ?? []).map((scheduledClass) => {
                      const available = scheduledClass.available;
                      const occupied = Math.max(0, scheduledClass.capacity - available);
                      const selected = value?.classId === scheduledClass.id && value.classDate === dateKey;
                      const current = currentSlot?.classId === scheduledClass.id && currentSlot.classDate === dateKey;
                      return (
                        <Button
                          key={`${scheduledClass.id}-${dateKey}`}
                          type="button"
                          variant="outline"
                          onClick={() => onChange({ classId: scheduledClass.id, classDate: dateKey, time: scheduledClass.time, name: scheduledClass.name })}
                          className={cn("h-auto w-full justify-start whitespace-normal px-2 py-1.5 text-left text-xs", selected && "border-primary bg-primary/10", !selected && current && "border-accent bg-accent/10")}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-1 font-semibold">
                              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{scheduledClass.time}</span>
                              {selected && <Check className="h-3.5 w-3.5 text-primary" />}
                            </span>
                            <span className="block truncate">{scheduledClass.name}</span>
                            <span className="mt-0.5 flex items-center gap-1 text-muted-foreground"><Users className="h-3 w-3" />{occupied}/{scheduledClass.capacity}</span>
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {value && (
        <div className="flex items-start gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-xs">
          <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <div><span className="font-medium">{t("rescheduleDialog.newSlot")}</span><br />{format(new Date(`${value.classDate}T12:00:00`), "EEEE d MMMM", { locale })} · {value.time} · {value.name}</div>
        </div>
      )}
    </div>
  );
}